import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  buscarClientesCheques,
  buscarClientesPorCuit,
  buscarProveedoresCheques,
  buscarProveedoresPorCuit,
  revisionCc,
  revisionCcLote,
} from "./ApiServices";
import { DECISION_CC } from "./cheques.constants";

const soloDigitos = (v) => String(v || "").replace(/\D/g, "");

const labelPersona = (p) => {
  if (!p) return "";
  const razon = p.razon_social || p.nombre || p.proveedor || p.cliente || "";
  const cuit = p.cuit || p.nro_cuit || "";
  const id = p.id ?? p.id_proveedor ?? p.id_cliente ?? "";
  return [razon, cuit ? `CUIT ${cuit}` : "", id ? `#${id}` : ""]
    .filter(Boolean)
    .join(" — ");
};

const idProveedorDe = (p) => p?.id ?? p?.id_proveedor ?? null;
const idClienteDe = (p) => p?.id ?? p?.id_cliente ?? null;

export default function ModalRevisionCC({
  open,
  onClose,
  cheques = [],
  onOk,
  modo = "revisar",
}) {
  const esReabrir = modo === "reabrir";
  const esRepudiar = modo === "repudiar";
  const [decision, setDecision] = useState(DECISION_CC.ACEPTAR);
  const [obs, setObs] = useState("");
  const [tipoAsignacion, setTipoAsignacion] = useState("proveedor");
  const [proveedor, setProveedor] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [opciones, setOpciones] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [avisoCuit, setAvisoCuit] = useState("");

  const esLote = cheques.length > 1;
  const chequeUnico = cheques[0] ?? null;
  const cuitCheque = soloDigitos(
    chequeUnico?.cuit_recibido || chequeUnico?.cuit_librador || ""
  );
  const pideAsignacion = !esReabrir && !esRepudiar && decision === DECISION_CC.ACEPTAR;

  useEffect(() => {
    if (!open) return;
    setDecision(
      esReabrir
        ? DECISION_CC.REABRIR
        : esRepudiar
          ? DECISION_CC.REPUDIAR
          : DECISION_CC.ACEPTAR
    );
    setObs("");
    setError("");
    setAvisoCuit("");
    setProveedor(null);
    setCliente(null);
    setOpciones([]);
    setBusqueda("");

    if (esLote || !chequeUnico) return;

    if (chequeUnico.id_proveedor) {
      setTipoAsignacion("proveedor");
      setProveedor({
        id: chequeUnico.id_proveedor,
        razon_social:
          chequeUnico.proveedor_razon_social ||
          chequeUnico.recibido_de ||
          "",
        cuit: chequeUnico.cuit_recibido || "",
      });
    } else if (chequeUnico.id_cliente) {
      setTipoAsignacion("cliente");
      setCliente({
        id: chequeUnico.id_cliente,
        razon_social:
          chequeUnico.cliente_razon_social || chequeUnico.recibido_de || "",
        cuit: chequeUnico.cuit_recibido || "",
      });
    } else {
      setTipoAsignacion("proveedor");
    }
  }, [open, chequeUnico, esLote, esReabrir, esRepudiar]);

  useEffect(() => {
    if (!open || esLote || !cuitCheque || !pideAsignacion) return undefined;

    let cancelado = false;
    (async () => {
      setBuscando(true);
      setAvisoCuit("");
      try {
        const data =
          tipoAsignacion === "cliente"
            ? await buscarClientesPorCuit(cuitCheque)
            : await buscarProveedoresPorCuit(cuitCheque);
        if (cancelado) return;
        setOpciones(data);
        setBusqueda(cuitCheque);
        if (tipoAsignacion === "cliente") {
          if (data.length === 1 && !chequeUnico?.id_cliente) {
            setCliente(data[0]);
          } else if (data.length === 0) {
            setAvisoCuit(
              `No hay cliente con CUIT ${cuitCheque}. Verificá el padrón de clientes.`
            );
          }
        } else if (data.length === 1 && !chequeUnico?.id_proveedor) {
          setProveedor(data[0]);
        } else if (data.length === 0) {
          setAvisoCuit(
            `No hay proveedor con CUIT ${cuitCheque}. Verificá el padrón de proveedores.`
          );
        }
      } catch {
        if (!cancelado) {
          setOpciones([]);
          setAvisoCuit(
            tipoAsignacion === "cliente"
              ? "No se pudo buscar clientes por el CUIT del cheque."
              : "No se pudo buscar proveedores por el CUIT del cheque."
          );
        }
      } finally {
        if (!cancelado) setBuscando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [
    open,
    esLote,
    cuitCheque,
    pideAsignacion,
    tipoAsignacion,
    chequeUnico?.id_proveedor,
    chequeUnico?.id_cliente,
  ]);

  useEffect(() => {
    if (!open || !pideAsignacion) return undefined;
    const q = busqueda.trim();
    const qDigitos = soloDigitos(q);

    if (qDigitos && qDigitos === cuitCheque && !esLote) return undefined;
    if (qDigitos.length < 8 && q.length < 2) return undefined;

    let cancelado = false;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const data =
          tipoAsignacion === "cliente"
            ? await buscarClientesCheques(q)
            : await buscarProveedoresCheques(q);
        if (!cancelado) setOpciones(data);
      } catch {
        if (!cancelado) setOpciones([]);
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 300);

    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [busqueda, open, cuitCheque, esLote, pideAsignacion, tipoAsignacion]);

  const cambiarTipo = (_e, value) => {
    if (!value) return;
    setTipoAsignacion(value);
    setOpciones([]);
    setBusqueda("");
    setAvisoCuit("");
    if (value === "cliente") setProveedor(null);
    else setCliente(null);
  };

  const guardar = async () => {
    const idProvSel = idProveedorDe(proveedor);
    const idCliSel = idClienteDe(cliente);
    const idProvCheque = chequeUnico?.id_proveedor ?? null;
    const idCliCheque = chequeUnico?.id_cliente ?? null;
    const idProveedor =
      tipoAsignacion === "proveedor"
        ? idProvSel ?? (!esLote ? idProvCheque : null)
        : null;
    const idCliente =
      tipoAsignacion === "cliente"
        ? idCliSel ?? (!esLote ? idCliCheque : null)
        : null;

    if (decision === DECISION_CC.ACEPTAR && !idProveedor && !idCliente) {
      setError("Seleccioná un proveedor o un cliente para aceptar");
      return;
    }

    setGuardando(true);
    setError("");
    const body = {
      decision,
      obs: obs.trim() || undefined,
      ...(idProveedor != null ? { id_proveedor: Number(idProveedor) } : {}),
      ...(idCliente != null ? { id_cliente: Number(idCliente) } : {}),
    };

    try {
      if (esLote) {
        await revisionCcLote({
          ids: cheques.map((c) => c.id),
          ...body,
        });
      } else {
        await revisionCc(chequeUnico.id, body);
      }
      onOk?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Error al guardar la revisión");
    } finally {
      setGuardando(false);
    }
  };

  const titulo = esReabrir
    ? esLote
      ? `Reabrir circulación (${cheques.length} cheques)`
      : `Reabrir — N° ${chequeUnico?.nro_cheque || chequeUnico?.id || ""}`
    : esRepudiar
      ? esLote
        ? `Repudiar (${cheques.length} cheques)`
        : `Repudiar — N° ${chequeUnico?.nro_cheque || chequeUnico?.id || ""}`
      : esLote
        ? `Revisión CC (${cheques.length} cheques)`
        : `Revisión CC — N° ${chequeUnico?.nro_cheque || chequeUnico?.id || ""}`;

  const valorAsignado = tipoAsignacion === "cliente" ? cliente : proveedor;

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {avisoCuit ? <Alert severity="warning">{avisoCuit}</Alert> : null}

          {esReabrir ? (
            <Alert severity="info">
              El cheque vuelve a circulación en pendiente de CC.
            </Alert>
          ) : esRepudiar ? (
            <Alert severity="warning">
              CC no acepta este eCheq. El cheque pasa a estado repudiado (no es
              un rechazo bancario ni una baja).
            </Alert>
          ) : (
            <TextField
              select
              SelectProps={{ native: true }}
              label="Decisión"
              size="small"
              fullWidth
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              disabled={guardando}
            >
              <option value={DECISION_CC.ACEPTAR}>Aceptar</option>
              <option value={DECISION_CC.PENDIENTE}>Dejar pendiente</option>
              <option value={DECISION_CC.REPUDIAR}>Repudiar</option>
            </TextField>
          )}

          {!esReabrir && !esRepudiar && decision === DECISION_CC.REPUDIAR ? (
            <Alert severity="warning">
              CC no acepta este eCheq. El cheque pasa a estado repudiado (no es
              un rechazo bancario ni una baja).
            </Alert>
          ) : null}

          {pideAsignacion ? (
            <>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={tipoAsignacion}
                onChange={cambiarTipo}
                disabled={guardando}
              >
                <ToggleButton value="proveedor">Proveedor</ToggleButton>
                <ToggleButton value="cliente">Cliente</ToggleButton>
              </ToggleButtonGroup>
              <Autocomplete
                options={opciones}
                value={valorAsignado}
                loading={buscando}
                onChange={(_e, value) => {
                  if (tipoAsignacion === "cliente") setCliente(value);
                  else setProveedor(value);
                }}
                onInputChange={(_e, value, reason) => {
                  if (reason === "reset") return;
                  setBusqueda(value);
                }}
                getOptionLabel={labelPersona}
                isOptionEqualToValue={(a, b) =>
                  String(a?.id ?? a?.id_proveedor ?? a?.id_cliente) ===
                  String(b?.id ?? b?.id_proveedor ?? b?.id_cliente)
                }
                noOptionsText={
                  buscando
                    ? "Buscando…"
                    : cuitCheque
                      ? `Sin ${tipoAsignacion === "cliente" ? "clientes" : "proveedores"} con CUIT ${cuitCheque}`
                      : "Sin opciones — ingresá un CUIT"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tipoAsignacion === "cliente" ? "Cliente" : "Proveedor"}
                    size="small"
                    placeholder="CUIT del cheque"
                    helperText={
                      cuitCheque && !esLote
                        ? `Mismo CUIT del cheque (${cuitCheque})`
                        : "Buscar por CUIT"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {buscando ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </>
          ) : null}

          <TextField
            label="Observaciones"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            disabled={guardando}
          />

          {!esLote && chequeUnico?.recibido_de ? (
            <Typography variant="caption" color="text.secondary">
              Recibido de: {chequeUnico.recibido_de}
              {chequeUnico.cuit_recibido
                ? ` (${chequeUnico.cuit_recibido})`
                : ""}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={esRepudiar || decision === DECISION_CC.REPUDIAR ? "error" : "primary"}
          onClick={guardar}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {esRepudiar || decision === DECISION_CC.REPUDIAR
            ? "Repudiar"
            : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
